import { env } from './env'
import { ApiError, offlineError } from './problem'
import type {
  Book,
  BookDetails,
  BookStats,
  Catalog,
  DownloadRecord,
  DownloadTicket,
  Page,
  ProblemDetails,
  Review,
  Subject,
  UiLanguage,
  UserProfile,
  Audience,
  ReviewStatus,
  ReviewWriteResult,
  Role,
} from './types'

/** Supplies the bearer token for the current session; set once at start-up. */
let tokenProvider: () => Promise<string | null> = async () => null

export function setTokenProvider(provider: () => Promise<string | null>): void {
  tokenProvider = provider
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Sent as If-None-Match so the server can answer 304 (§15.3). */
  etag?: string | null
  signal?: AbortSignal
  /** Skip the browser's HTTP cache and ask the server again. */
  fresh?: boolean
}

export interface ApiResponse<T> {
  data: T
  etag: string | null
  notModified: boolean
}

async function toProblem(response: Response): Promise<ProblemDetails> {
  const fallback: ProblemDetails = {
    type: 'https://library.itpu.uz/problems/unexpected',
    title: response.statusText || 'Request failed',
    status: response.status,
  }
  try {
    const body = (await response.json()) as Partial<ProblemDetails>
    return {
      type: body.type ?? fallback.type,
      title: body.title ?? fallback.title,
      status: body.status ?? response.status,
      ...(body.detail !== undefined ? { detail: body.detail } : {}),
      ...(body.instance !== undefined ? { instance: body.instance } : {}),
      ...(body.traceId !== undefined ? { traceId: body.traceId } : {}),
    }
  } catch {
    return fallback
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
  const token = await tokenProvider()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.etag) headers['If-None-Match'] = options.etag

  let response: Response
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.fresh ? { cache: 'no-cache' as const } : {}),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    // fetch only rejects for transport failures, which the UI reports as offline.
    throw offlineError()
  }

  const etag = response.headers.get('ETag')

  if (response.status === 304) {
    return { data: undefined as T, etag, notModified: true }
  }

  if (!response.ok) {
    const retryAfter = Number(response.headers.get('Retry-After') ?? '')
    throw new ApiError(await toProblem(response), Number.isFinite(retryAfter) ? retryAfter : null)
  }

  if (response.status === 204) {
    return { data: undefined as T, etag, notModified: false }
  }

  return { data: (await response.json()) as T, etag, notModified: false }
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    sp.set(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/** Member-facing endpoints (§9.1). */
export const api = {
  health: () => request<{ status: string }>('/health'),

  me: () => request<UserProfile>('/me'),

  updateMe: (patch: { preferredLanguage: UiLanguage }) =>
    request<UserProfile>('/me', { method: 'PATCH', body: patch }),

  /** Passes the cached version and ETag so an unchanged catalogue costs a 304. */
  catalog: (version?: number, etag?: string | null) =>
    request<Catalog>(`/catalog${qs({ version })}`, { etag: etag ?? null }),

  /**
   * The detail-only half of every record, cached like the catalogue and keyed
   * to the same version. Split out of `/catalog` to keep the first-paint
   * payload inside the §15.5 budget.
   */
  bookDetails: (version?: number, etag?: string | null) =>
    request<Record<string, BookDetails>>(`/catalog/details${qs({ version })}`, {
      etag: etag ?? null,
    }),

  /**
   * Always past the browser cache. The server marks `/stats` `max-age=60`, and
   * the refetch after a download or a review would otherwise be answered from
   * that cache — the reader saw last minute's counters, then on their next
   * click both counters jumped at once, which read as a repeat download
   * moving the unique count. React Query's own 60 s staleTime already sets
   * the cadence, so the HTTP cache bought nothing here.
   */
  stats: (ids?: string[]) =>
    request<BookStats[]>(`/stats${qs({ ids: ids?.join(',') })}`, { fresh: true }),

  /** §11.7 — one read token for the whole covers container, not one per image. */
  coversAccess: () => request<CoversAccess>('/covers/access'),

  book: (bookId: string) =>
    request<{
      book: Book & BookDetails
      stats: BookStats
      myReview: Review | null
      myDownload: DownloadRecord | null
    }>(`/books/${encodeURIComponent(bookId)}`),

  subjects: () => request<Subject[]>('/subjects'),

  subject: (subjectId: string) =>
    request<{ subject: Subject; primary: string[]; related: string[] }>(
      `/subjects/${encodeURIComponent(subjectId)}`,
    ),

  /** Issues a 10-minute SAS, or the publisher URL for an external title (FR-5.1). */
  download: (bookId: string) =>
    request<DownloadTicket>(`/books/${encodeURIComponent(bookId)}/download`, { method: 'POST' }),

  reviews: (
    bookId: string,
    params: { audience?: Audience; sort?: string; page?: number; pageSize?: number } = {},
  ) => request<Page<Review>>(`/books/${encodeURIComponent(bookId)}/reviews${qs({ ...params })}`),

  putMyReview: (bookId: string, body: { rating: number; body?: string; audience: Audience }) =>
    request<ReviewWriteResult>(`/books/${encodeURIComponent(bookId)}/reviews/me`, {
      method: 'PUT',
      body,
    }),

  deleteMyReview: (bookId: string) =>
    request<ReviewWriteResult>(`/books/${encodeURIComponent(bookId)}/reviews/me`, {
      method: 'DELETE',
    }),

  reportReview: (bookId: string, userOid: string, reason: string) =>
    request<void>(
      `/books/${encodeURIComponent(bookId)}/reviews/${encodeURIComponent(userOid)}/report`,
      { method: 'POST', body: { reason } },
    ),

  myDownloads: (page = 1) => request<Page<DownloadRecord & { bookTitle: string }>>(
    `/me/downloads${qs({ page })}`,
  ),

  myReviews: (page = 1) =>
    request<Page<Review & { bookTitle: string }>>(`/me/reviews${qs({ page })}`),
}

/** §11.7 — where the cover thumbnails are, and what opens them. */
export interface CoversAccess {
  baseUrl: string
  token: string
  expiresAt: string
}

export interface AdminOverview {
  bookCount: number
  subjectCount: number
  userCount: number
  teacherCount: number
  downloadsToday: number
  downloadsThisWeek: number
  downloadsThisMonth: number
  downloadsAllTime: number
  uniqueReadersThisMonth: number
  openReports: number
  booksWithoutSubject: number
  subjectsBelowTarget: number
  booksWithMissingFile: number
  /** Hosted titles whose file is a different size from their record (§16.4). */
  booksWithFileMismatch: number
  /** When the weekly integrity check last ran; absent until it has. */
  integrityCheckedAt?: string | null
  storageUsedBytes: number
  topBooks: Array<{ bookId: string; title: string; downloads: number }>
  topSubjects: Array<{ subjectId: string; name: string; downloads: number }>
  topReaders: Array<{ oid: string; displayName: string; downloads: number }>
  downloadsOverTime: Array<{ bucket: string; downloads: number; uniqueUsers: number }>
}

export interface AdminUserRow extends UserProfile {
  downloadCount: number
  reviewCount: number
  lastSeenAt: string
}

export interface ModerationItem {
  bookId: string
  bookTitle: string
  review: Review
  reports: Array<{ reporterOid: string; reason: string; createdAt: string }>
  state: 'open' | 'resolved'
}

export interface AuditEntry {
  id: string
  actorOid: string
  actorEmail: string
  action: string
  targetType: string
  targetId: string
  before: unknown
  after: unknown
  reason?: string | null
  occurredAt: string
  ip?: string | null
  userAgent?: string | null
}

/**
 * What an administrator may set on a book — deliberately not `Partial<Book>`.
 *
 * `availability`, `sizeBytes` and `sha256` are derived from the file that was
 * uploaded (§2.1), and the statistics are nobody's to type in, so none of them
 * appears here.
 */
export interface AdminBookInput {
  id?: string
  title: string
  authors?: string[]
  edition?: string
  yearPublished?: number
  originalYear?: number
  language: string
  publisher?: string
  level: string
  description?: string
  keywords?: string[]
  collection: string
  format: string
  sourceSite?: string
  sourcePageUrl?: string
  sourceDownloadUrl?: string
  licenseName?: string
  licenseUrl?: string
  usageRestrictions?: string
  accessType: string
  primarySubjects?: string[]
  relatedSubjects?: string[]
}

/**
 * Admin endpoints (§9.2). Every one of these is re-checked server-side.
 *
 * Served under `/manage`, not the `/admin` of §9.2: the Functions host reserves
 * `admin` for its own management API and refuses to load a function that uses
 * it, whatever the route prefix. The console's own browser URLs are unaffected
 * and stay `/admin/...` — those are the SPA's routes, not the API's.
 */
export const adminApi = {
  overview: () => request<AdminOverview>('/manage/overview'),

  books: (params: { q?: string; status?: string; page?: number; pageSize?: number } = {}) =>
    request<Page<Book & { downloadsTotal: number; subjectCount: number }>>(
      `/manage/books${qs({ ...params })}`,
    ),

  createBook: (body: AdminBookInput) =>
    // Returns the whole record rather than only its id: the caller needs the
    // id to upload a file against, and the record to show what was stored.
    request<Book>('/manage/books', { method: 'POST', body }),

  updateBook: (id: string, body: AdminBookInput) =>
    request<Book>(`/manage/books/${encodeURIComponent(id)}`, { method: 'PUT', body }),

  archiveBook: (id: string) =>
    request<void>(`/manage/books/${encodeURIComponent(id)}/archive`, { method: 'POST' }),

  unarchiveBook: (id: string) =>
    request<void>(`/manage/books/${encodeURIComponent(id)}/unarchive`, { method: 'POST' }),

  deleteBook: (id: string, confirm: string, reason: string) =>
    request<void>(`/manage/books/${encodeURIComponent(id)}${qs({ confirm })}`, {
      method: 'DELETE',
      body: { reason },
    }),

  /** 30-minute write SAS so a 290 MB file never passes through a Function (FR-9.3). */
  uploadUrl: (id: string, fileName: string, sizeBytes: number) =>
    request<{ url: string; expiresAt: string; blobPath: string }>(
      `/manage/books/${encodeURIComponent(id)}/upload-url`,
      { method: 'POST', body: { fileName, sizeBytes } },
    ),

  fileCommitted: (id: string, body: { blobPath: string; sizeBytes: number }) =>
    request<Book>(`/manage/books/${encodeURIComponent(id)}/file-committed`, {
      method: 'POST',
      body,
    }),

  adjustDownloads: (id: string, delta: number, reason: string) =>
    request<BookStats>(`/manage/books/${encodeURIComponent(id)}/downloads/adjust`, {
      method: 'POST',
      body: { delta, reason },
    }),

  setBookSubjects: (id: string, body: { primary: string[]; related: string[] }) =>
    request<void>(`/manage/books/${encodeURIComponent(id)}/subjects`, { method: 'PUT', body }),

  subjects: () => request<Subject[]>('/manage/subjects'),

  createSubject: (body: Partial<Subject>) =>
    request<Subject>('/manage/subjects', { method: 'POST', body }),

  updateSubject: (id: string, body: Partial<Subject>) =>
    request<Subject>(`/manage/subjects/${encodeURIComponent(id)}`, { method: 'PUT', body }),

  deleteSubject: (id: string, confirm: string) =>
    request<void>(`/manage/subjects/${encodeURIComponent(id)}${qs({ confirm })}`, {
      method: 'DELETE',
    }),

  setSubjectBooks: (id: string, body: { primary: string[]; related: string[] }) =>
    request<void>(`/manage/subjects/${encodeURIComponent(id)}/books`, { method: 'PUT', body }),

  users: (params: { q?: string; role?: Role; blocked?: boolean; page?: number; pageSize?: number } = {}) =>
    request<Page<AdminUserRow>>(`/manage/users${qs({ ...params })}`),

  updateUser: (oid: string, body: { roles?: Role[]; isBlocked?: boolean; blockedReason?: string }) =>
    request<AdminUserRow>(`/manage/users/${encodeURIComponent(oid)}`, { method: 'PATCH', body }),

  moderation: (params: { state?: 'open' | 'resolved'; sort?: string } = {}) =>
    request<Page<ModerationItem>>(`/manage/moderation${qs({ ...params })}`),

  moderateReview: (
    bookId: string,
    userOid: string,
    body: { status: ReviewStatus; reason: string },
  ) =>
    request<Review>(
      `/manage/reviews/${encodeURIComponent(bookId)}/${encodeURIComponent(userOid)}`,
      { method: 'PATCH', body },
    ),

  stats: (params: {
    from: string
    to: string
    groupBy: 'day' | 'week' | 'month'
    dimension: 'book' | 'subject' | 'collection' | 'language' | 'role'
  }) =>
    request<{
      series: Array<{ bucket: string; downloads: number; uniqueUsers: number }>
      breakdown: Array<{ key: string; label: string; downloads: number; uniqueUsers: number }>
    }>(`/manage/stats${qs({ ...params })}`),

  exportStats: (body: { from: string; to: string; dimension: string }) =>
    request<{ url: string; expiresAt: string }>('/manage/stats/export', { method: 'POST', body }),

  audit: (
    params: {
      from?: string
      to?: string
      actor?: string
      action?: string
      page?: number
      pageSize?: number
    } = {},
  ) =>
    request<Page<AuditEntry>>(`/manage/audit${qs({ ...params })}`),
}
